import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { getAllInvoicesAPI, getInvoiceDashboardStatsAPI } from "../../../../Redux/ApiCalls/Dashboard/InvoiceAPI";
import { InvoiceDashboardStats } from "../../../../Data/invoice.dto";
import { getOrderTransactionsAPI } from "../../../../Redux/ApiCalls/Dashboard/OrderAPI";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Eye,
  FileText,
  Sparkles,
  Wallet,
  IndianRupee,
  Receipt,
  ArrowUpRight,
  Calendar,
} from "lucide-react";
import { device } from "../../../atoms/Utlis/media";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";

const GRADIENT = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";
const PURPLE = "#c94daa";
const CHART_ORANGE = "#F2AD6D";
const CHART_PURPLE = "#D776A4";
const CHART_GRADIENT = "linear-gradient(180.14deg, #F2AD6D 0.11%, #D776A4 99.87%)";

const Page = styled.div`
  width: 100%;
  min-height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  overflow: visible;
  flex-shrink: 0;
  font-family: Urbanist, sans-serif;
  background:
    radial-gradient(ellipse 70% 40% at 0% 0%, rgba(201, 77, 170, 0.05), transparent 55%),
    radial-gradient(ellipse 50% 35% at 100% 0%, rgba(255, 201, 81, 0.06), transparent 50%),
    transparent;

  @media ${device.tablet} {
    padding: 16px;
    gap: 14px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const ContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  overflow: visible;
`;

const FilterCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 20px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.04);
  flex-shrink: 0;
`;

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(23, 23, 23, 0.55);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
`;

const FilterDateWrap = styled.div`
  min-width: 160px;
`;

const PrimaryBtn = styled.button`
  background: ${GRADIENT};
  border: none;
  color: #fff;
  padding: 10px 18px;
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.95;
  }
`;

const SecondaryBtn = styled.button`
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.12);
  color: rgba(23, 23, 23, 0.72);
  padding: 10px 18px;
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;

  @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;

  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  gap: 12px;
  color: rgba(23, 23, 23, 0.5);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  border: 1px solid rgba(23, 23, 23, 0.06);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 18px 22px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const GaugeWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 20px 0;
  position: relative;
`;

const GaugeCenterText = styled.div`
  text-align: center;
  margin-top: -32px;
  margin-bottom: 12px;
`;

const GaugeTotalLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const GaugeTotalValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 21px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.86);
`;

const ServiceLegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 20px 18px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`;

const ServiceLegendLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #171717;
`;

const LegendDot = styled.span<{ $color?: string; $gradient?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => (p.$gradient ? CHART_GRADIENT : p.$color)};
  display: inline-block;
  flex-shrink: 0;
`;

const ServiceLegendAmount = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
`;

const ChartWrap = styled.div`
  padding: 10px 16px 16px;
  height: 240px;
`;

const IncomeExpenseWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 22px 22px;
  gap: 12px;
`;

const NoDataBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.38);
  background: rgba(23, 23, 23, 0.02);
  border-radius: 14px;
  border: 1px dashed rgba(23, 23, 23, 0.08);
`;

const IncomeTotalsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 14px;
  overflow: hidden;
`;

const IncomeTile = styled.div<{ $tone?: string }>`
  padding: 16px 20px;
  border-right: 1px solid rgba(23, 23, 23, 0.08);
  background: ${(p) => (p.$tone ? `${p.$tone}08` : "#fff")};

  &:last-child {
    border-right: none;
  }
`;

const IncomeTileValue = styled.div<{ $tone?: string }>`
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: ${(p) => p.$tone || "rgba(23, 23, 23, 0.86)"};
  letter-spacing: -0.02em;
`;

const IncomeTileLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.45);
  margin-top: 4px;
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  padding: 4px 22px 22px;
`;

const BreakdownTile = styled.div`
  padding: 16px 18px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const ViewAllBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 10px;
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  padding: 0 22px 20px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Urbanist, sans-serif;

  th {
    text-align: left;
    padding: 13px 18px;
    background: rgba(23, 23, 23, 0.02);
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
  }

  th span {
    background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 80%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    display: inline-block;
  }

  td {
    padding: 13px 18px;
    font-size: 13px;
    font-weight: 500;
    color: rgba(23, 23, 23, 0.82);
    border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: rgba(201, 77, 170, 0.04);
  }
`;

const CustomerCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CustomerName = styled.div`
  font-weight: 600;
  color: #171717;
`;

const CustomerMobile = styled.div`
  font-size: 11px;
  color: rgba(23, 23, 23, 0.45);
`;

const dashboardStatusColors: Record<string, { bg: string; color: string }> = {
  paid: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  fully_paid: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Active: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Completed: { bg: "rgba(53,183,200,0.12)", color: "#35b7c8" },
  partially_paid: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  sent: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  draft: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  Pending: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  not_paid: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  unpaid: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  cancelled: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  overdue: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  Cancelled: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
};

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => dashboardStatusColors[p.$status]?.bg ?? dashboardStatusColors[p.$status?.toLowerCase()]?.bg ?? "rgba(23,23,23,0.08)"};
  color: ${(p) => dashboardStatusColors[p.$status]?.color ?? dashboardStatusColors[p.$status?.toLowerCase()]?.color ?? "#171717"};
`;

const EyeBtn = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.15) 0%, rgba(206, 86, 176, 0.15) 100%);
  color: #c94daa;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
    color: #fff;
    transform: translateY(-1px);
  }
`;

const TooltipBox = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 8px 12px;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.86);
`;

const formatINR = (val: number) =>
  "₹" + val.toLocaleString("en-IN", { minimumFractionDigits: 2 });

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox>
      <div style={{ color: "#9ca3af", marginBottom: 4, fontSize: 11 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}: {formatINR(p.value)}
        </div>
      ))}
    </TooltipBox>
  );
};

const InvoiceDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedBranch } = useAppSelector((state) => state.auth);

  const [stats, setStats] = useState<InvoiceDashboardStats | null>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchData = async (filters?: { from_date?: string; to_date?: string }) => {
    setLoading(true);
    try {
      const statsRes = await getInvoiceDashboardStatsAPI({
        ...filters,
        branch_id: selectedBranch?.id,
      });
      setStats(statsRes);

      const invoicesRes = await dispatch(getAllInvoicesAPI({ page_size: 5, branch_id: selectedBranch?.id })).unwrap();
      setInvoices(invoicesRes?.results || invoicesRes || []);

      const txRes = await getOrderTransactionsAPI({
        page_size: 1000,
        ...(filters?.from_date ? { from_date: filters.from_date } : {}),
        ...(filters?.to_date ? { to_date: filters.to_date } : {}),
        branch_id: selectedBranch?.id,
      });
      setTransactions(txRes?.results || txRes || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch, selectedBranch?.id]);

  const handleApplyFilter = () => {
    fetchData({
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    });
  };

  const handleClearFilter = () => {
    setFromDate("");
    setToDate("");
    fetchData();
  };

  const metrics = useMemo(() => {
    const totalInvoiced = Number(stats?.total_amount || 0);
    const paymentReceived = Number(stats?.total_paid || 0);
    const outstandingBalance = Number(stats?.total_remaining || 0);

    let totalExpense = 0;
    let totalIncome = 0;

    transactions.forEach((t) => {
      if (t.type === "Income") {
        totalIncome += Number(t.credit_amount || 0);
      } else if (t.type === "Expense") {
        totalExpense += Number(t.debit_amount || 0);
      }
    });

    return { totalInvoiced, paymentReceived, outstandingBalance, totalExpense, totalIncome };
  }, [stats, transactions]);

  const statusDistribution = useMemo(() => {
    if (!stats?.payment_status_breakdowns) return [];

    const labelMap: Record<string, string> = {
      paid: "Paid",
      fully_paid: "Paid",
      partially_paid: "Partially Paid",
      not_paid: "Unpaid",
      unpaid: "Unpaid",
    };

    const colorMap: Record<string, string> = {
      paid: "#31a66a",
      fully_paid: "#31a66a",
      partially_paid: "#F2AD6D",
      not_paid: "#e44862",
      unpaid: "#e44862",
    };

    return stats.payment_status_breakdowns.map((b) => ({
      name: labelMap[b.payment_status] || b.payment_status,
      value: b.count,
      amount: Number(b.amount || 0),
      color: colorMap[b.payment_status] || "#9ca3af",
    }));
  }, [stats]);

  const chartData = useMemo(() => {
    if (!stats?.monthly_trends) return [];

    const MONTH_NAMES = [
      "", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    return stats.monthly_trends.map((t) => {
      let name = String(t.month);
      const monthNum = Number(t.month);
      if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
        name = MONTH_NAMES[monthNum];
      }
      return {
        name,
        Amount: Number(t.total_amount || 0),
        Paid: Number(t.paid_amount || 0),
        Remaining: Number(t.remaining_amount || 0),
      };
    });
  }, [stats]);

  if (loading && !stats) {
    return (
      <Page>
        <LoadingWrap>
          <Sparkles size={28} color="#c94daa" />
          Loading dashboard...
        </LoadingWrap>
      </Page>
    );
  }

  return (
    <Page>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <div className="flex flex-col">
            <HeroBadge>
              <Sparkles size={13} />
              Billing insights
            </HeroBadge>
            <PageTitle className="linear-text">Invoice Dashboard</PageTitle>
            <PageSubtitle>
              Overview of invoices, payment collections, and expenses.
            </PageSubtitle>
          </div>
        </HeroContent>
      </Hero>

      <FilterCard>
        <FilterLeft>
          <Calendar size={14} />
          Date range
        </FilterLeft>
        <FilterControls>
          <FilterGroup>
            <FilterLabel>From</FilterLabel>
            <FilterDateWrap>
              <BrandDatePicker
                value={fromDate}
                onChange={(v) => setFromDate(v)}
                size="compact"
              />
            </FilterDateWrap>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>To</FilterLabel>
            <FilterDateWrap>
              <BrandDatePicker
                value={toDate}
                onChange={(v) => setToDate(v)}
                size="compact"
              />
            </FilterDateWrap>
          </FilterGroup>
          <PrimaryBtn type="button" onClick={handleApplyFilter}>Apply</PrimaryBtn>
          <SecondaryBtn type="button" onClick={handleClearFilter}>Clear</SecondaryBtn>
        </FilterControls>
      </FilterCard>

      <ContentWrap>
        <StatsGrid>
          <WalletHero>
            <WalletIconWrap>
              <FileText size={16} />
            </WalletIconWrap>
            <WalletHeroBody>
              <WalletHeroValue>{formatINR(metrics.totalInvoiced)}</WalletHeroValue>
              <WalletHeroLabel>Total Invoiced</WalletHeroLabel>
            </WalletHeroBody>
          </WalletHero>

          <StatCard $tone="#31a66a">
            <StatIcon $tone="#31a66a">
              <Wallet size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{formatINR(metrics.paymentReceived)}</StatValue>
              <StatLabel>Payment Received</StatLabel>
            </StatBody>
          </StatCard>

          <StatCard $tone="#e44862">
            <StatIcon $tone="#e44862">
              <IndianRupee size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{formatINR(metrics.outstandingBalance)}</StatValue>
              <StatLabel>Outstanding Balance</StatLabel>
            </StatBody>
          </StatCard>

          <StatCard $tone="#35b7c8">
            <StatIcon $tone="#35b7c8">
              <Receipt size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{formatINR(metrics.totalExpense)}</StatValue>
              <StatLabel>Invoice Expense</StatLabel>
            </StatBody>
          </StatCard>
        </StatsGrid>

        <TwoCol>
          <Panel>
            <PanelHeader>
              <PanelTitle>Invoicing Trend (Overtime)</PanelTitle>
            </PanelHeader>
            <ChartWrap>
              <ResponsiveContainer width="100%" height="100%">
                {chartData.length > 0 ? (
                  <AreaChart data={chartData} margin={{ top: 12, right: 12, left: 16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={PURPLE} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={PURPLE} stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="themeLineGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0.11%" stopColor={CHART_ORANGE} />
                        <stop offset="99.87%" stopColor={CHART_PURPLE} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(23,23,23,0.06)" />
                    <XAxis dataKey="name" stroke="rgba(23,23,23,0.4)" fontSize={11} tickLine={false} />
                    <YAxis stroke="rgba(23,23,23,0.4)" fontSize={11} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="Amount" name="Amount" stroke="url(#themeLineGrad)" strokeWidth={2} fillOpacity={1} fill="url(#colorAmt)" />
                  </AreaChart>
                ) : (
                  <NoDataBox style={{ height: "100%" }}>No trend data available.</NoDataBox>
                )}
              </ResponsiveContainer>
            </ChartWrap>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Payment Status Distribution</PanelTitle>
            </PanelHeader>
            <GaugeWrap style={{ height: 180, display: "flex", justifyContent: "center" }}>
              {statusDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="70%"
                      startAngle={180}
                      endAngle={0}
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <NoDataBox style={{ width: "100%", margin: "0 20px" }}>No payment status data available.</NoDataBox>
              )}
              <GaugeCenterText>
                <GaugeTotalLabel>Total Invoices</GaugeTotalLabel>
                <GaugeTotalValue>{stats?.total_invoices || 0}</GaugeTotalValue>
              </GaugeCenterText>
            </GaugeWrap>
            <ServiceLegendRow>
              {statusDistribution.map((entry, index) => (
                <div key={index} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <ServiceLegendLeft>
                    <LegendDot $color={entry.color} />
                    <span style={{ fontSize: 12 }}>{entry.name}</span>
                  </ServiceLegendLeft>
                  <ServiceLegendAmount style={{ fontSize: 13, marginTop: 4 }}>
                    {entry.value} invoices
                  </ServiceLegendAmount>
                </div>
              ))}
            </ServiceLegendRow>
          </Panel>
        </TwoCol>

        <TwoCol>
          <Panel>
            <PanelHeader>
              <PanelTitle>Income Vs Expense Statistics</PanelTitle>
            </PanelHeader>
            <IncomeExpenseWrap>
              {transactions.length === 0 && <NoDataBox>No income/expense data available.</NoDataBox>}
              <IncomeTotalsRow>
                <IncomeTile $tone="#31a66a">
                  <IncomeTileValue $tone="#31a66a">{formatINR(metrics.totalIncome)}</IncomeTileValue>
                  <IncomeTileLabel>Total Income</IncomeTileLabel>
                </IncomeTile>
                <IncomeTile $tone="#e44862">
                  <IncomeTileValue $tone="#e44862">{formatINR(metrics.totalExpense)}</IncomeTileValue>
                  <IncomeTileLabel>Total Expense</IncomeTileLabel>
                </IncomeTile>
              </IncomeTotalsRow>
            </IncomeExpenseWrap>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Collections by Payment Method</PanelTitle>
            </PanelHeader>
            <BreakdownGrid>
              {(!stats?.payment_methods_breakdown || stats.payment_methods_breakdown.length === 0) && (
                <NoDataBox style={{ gridColumn: "1 / -1" }}>
                  No payment collections found for this date range.
                </NoDataBox>
              )}
              {stats?.payment_methods_breakdown?.map((item, idx) => (
                <BreakdownTile key={idx}>
                  <IncomeTileValue>{formatINR(Number(item.amount || 0))}</IncomeTileValue>
                  <IncomeTileLabel style={{ textTransform: "capitalize" }}>
                    {item.method.replace("_", " ")} ({item.count} payments)
                  </IncomeTileLabel>
                </BreakdownTile>
              ))}
            </BreakdownGrid>
          </Panel>
        </TwoCol>

        <Panel>
          <PanelHeader style={{ paddingBottom: 12 }}>
            <PanelTitle>Invoices by Status</PanelTitle>
          </PanelHeader>
          <BreakdownGrid>
            {(!stats?.invoice_status_breakdowns || stats.invoice_status_breakdowns.length === 0) && (
              <NoDataBox style={{ gridColumn: "1 / -1" }}>No invoice status data available.</NoDataBox>
            )}
            {stats?.invoice_status_breakdowns?.map((item, idx) => (
              <BreakdownTile key={idx}>
                <IncomeTileValue>{formatINR(Number(item.amount || 0))}</IncomeTileValue>
                <IncomeTileLabel style={{ textTransform: "capitalize" }}>
                  {item.invoice_status} ({item.count} invoices)
                </IncomeTileLabel>
              </BreakdownTile>
            ))}
          </BreakdownGrid>
        </Panel>

        <Panel>
          <PanelHeader style={{ paddingBottom: 12 }}>
            <PanelTitle>Latest Invoices</PanelTitle>
            <ViewAllBtn type="button" onClick={() => navigate("/dashboard/invoice")}>
              View All Invoices <ArrowUpRight size={14} />
            </ViewAllBtn>
          </PanelHeader>
          <TableWrap>
            <StyledTable>
              <thead>
                <tr>
                  <th><span>Invoice No</span></th>
                  <th><span>Date</span></th>
                  <th><span>Customer</span></th>
                  <th><span>Status</span></th>
                  <th><span>Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {invoices.slice(0, 5).map((inv, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: 700, color: "#171717" }}>{inv.invoice_no}</td>
                    <td>{inv.invoice_date}</td>
                    <td>
                      <CustomerCell>
                        <CustomerName>{inv.customer_name}</CustomerName>
                        <CustomerMobile>{inv.customer_phone}</CustomerMobile>
                      </CustomerCell>
                    </td>
                    <td>
                      <StatusBadge $status={inv.payment_status}>
                        {inv.payment_status === "partially_paid" ? "Partial" : inv.payment_status}
                      </StatusBadge>
                    </td>
                    <td>
                      <EyeBtn
                        type="button"
                        aria-label="View invoice"
                        onClick={() => navigate(`/dashboard/invoice/view?id=${inv.id}`)}
                      >
                        <Eye size={15} />
                      </EyeBtn>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 24, color: "rgba(23,23,23,0.4)" }}>
                      No invoices found.
                    </td>
                  </tr>
                )}
              </tbody>
            </StyledTable>
          </TableWrap>
        </Panel>
      </ContentWrap>
    </Page>
  );
};

export default InvoiceDashboardPage;
